/* -------------------------------------------------------------
   ConvoDumpModal.js — uses api.defaults.baseURL for streaming
   ------------------------------------------------------------- */
import React, { useState, useEffect } from "react";
import Papa  from "papaparse";
import api   from "../api";

/* ---------- 1. Cookie spec (unchanged) ---------- */
const SPEC = {
  openai: [
    { id:"openai_session_token",  label:"__Secure-next-auth.session-token",   req:false },
    { id:"openai_session_token0", label:"__Secure-next-auth.session-token.0", req:false },
    { id:"openai_session_token1", label:"__Secure-next-auth.session-token.1", req:false },
    { id:"openai_csrf_token",     label:"__Host-next-auth.csrf-token",        req:true  },
    { id:"cloudflare_clearance",  label:"cf_clearance",                       req:false },
    { id:"cloudflare_bm",         label:"_cf_bm",                             req:false }
  ],
  anthropic:[ { id:"anthropic_session", label:"session", req:true } ],
  google:[
    { id:"google_nid",    label:"NID",     req:true },
    { id:"google_sapisid",label:"SAPISID", req:false }
  ]
};

const Field = ({ id,label,value,onChange,req }) => (
  <div className="flex flex-col mb-2 w-full">
    <label htmlFor={id} className="text-green-400 text-xs mb-1">
      {label}{req && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      id={id}
      className="input-terminal"
      value={value}
      onChange={e => onChange(e.target.value)}
      autoComplete="off"
    />
  </div>
);

export default function ConvoDumpModal({ open,onClose }) {
  /* ---------- 2. State ---------- */
  const [step,setStep]       = useState(0);
  const [provider,setProv]   = useState("");
  const [cState,setCState]   = useState({});
  const [accountCookie, setAccountCookie] = useState("");

  const [count,setCount]     = useState(10);
  const [titles,setTitles]   = useState([]);
  const [chats,setChats]     = useState([]);
  const [idx,setIdx]         = useState(0);

  const [topics,setTopics]         = useState([]);
  const [modelCodes,setModelCodes] = useState([]);

  /* ---------- 3. Load topics + model list ---------- */
  useEffect(()=>{ api.get("/topics").then(r=>setTopics(r.data||[])); },[]);
  useEffect(()=>{
    fetch("/assets/models.csv")
      .then(r=>r.text())
      .then(txt=>setModelCodes(Papa.parse(txt,{header:true}).data));
  },[]);

  if(!open) return null;

  /* ---------- 4. Build cookies ---------- */
  const buildCookies = () => {
    if(provider==="openai"){
      const bag=[];
      const s  = cState.openai_session_token?.trim();
      const s0 = cState.openai_session_token0?.trim();
      if(s)  bag.push({ name: "__Host-next-auth.session-token", value: s, domain: ".openai.com", path: "/", httpOnly: true, secure: true });
      if(s0) bag.push({ name: "__Secure-next-auth.session-token", value: s0, domain: ".openai.com", path: "/", httpOnly: true, secure: true });
      if(accountCookie) bag.push({ name: "_account", value: accountCookie.trim(), domain: ".openai.com", path: "/", httpOnly: false, secure: true });
      return bag;
    }
    if(provider==="anthropic"){
      const s = cState.anthropic_session_token?.trim();
      if(s) return [{ name: "sessionKey", value: s, domain: ".claude.ai", path: "/", httpOnly: true, secure: true }];
      return [];
    }
    if(provider==="gemini"){
      const s = cState.gemini_session_token?.trim();
      if(s) return [{ name: "__Secure-1PSID", value: s, domain: ".google.com", path: "/", httpOnly: true, secure: true }];
      return [];
    }
    return Object.entries(cState)
      .filter(([,v])=>v&&v.trim())
      .map(([k,v])=>({name:k,value:v.trim()}));
  };

  const cookiesValid = () => {
    if(provider==="openai"){
      const s  = cState.openai_session_token?.trim();
      const s0 = cState.openai_session_token0?.trim();
      const s1 = cState.openai_session_token1?.trim();
      return ((s||(s0&&s1)) && cState.openai_csrf_token?.trim());
    }
    return SPEC[provider].every(f=>!f.req || cState[f.id]?.trim());
  };

  /* ---------- 5. beginScrape (fetch with full base URL) ---------- */
  const beginScrape = async n => {
  setCount(n);
  setStep(3); // Show progress UI
  setTitles([]);
  setChats([]);
  try {
    // Use api.post, NOT fetch, and do NOT manually add baseURL
    const res = await api.post('/convodump/scrape', {
      provider,
      count: n,
      cookies: buildCookies(),
    });
    if (res.data && Array.isArray(res.data.chats)) {
      setChats(res.data.chats);
      setTitles(res.data.chats.map(c => c.title));
      setStep(4); // Review step
    } else {
      alert('No chats returned');
      setStep(2);
    }
  } catch (err) {
    alert('Scraper error: ' + (err.response?.data?.error || err.message));
    setStep(2);
  }
};

  /* ---------- 6. Review helpers ---------- */
  const chat   = chats[idx]||{};
  const update = p=>setChats(cs=>cs.map((c,i)=>i===idx?{...c,...p}:c));

  const save = async keep=>{
    if(keep)    await api.post("/convodump/import", chat);   
    await api.post("/convodump/rename",{
      url: chat.url,
      newTitle:`..${chat.title||"untitled"}`
    });
    idx+1<chats.length? setIdx(i=>i+1) : onClose();
  };

  /* ---------- 7. UI (unchanged) ---------- */
  return(
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
    <div className="bg-green-950 border border-green-700 p-4 w-full max-w-xl relative">
      <button className="absolute top-2 right-2 text-green-400" onClick={onClose}>×</button>

      {step===0&&(
        <div className="flex flex-col items-center p-6">
          <h3 className="text-green-300 mb-4">Select provider</h3>
          {["openai","anthropic","google"].map(p=>(
            <button key={p} className="btn-primary mb-2"
                    onClick={()=>{setProv(p);setStep(1);}}>{p.toUpperCase()}</button>
          ))}
        </div>
      )}

      {step===1&&(
        <div className="p-6 max-w-md mx-auto">
          <h3 className="text-green-300 mb-4">Paste cookies for {provider.toUpperCase()}</h3>
          {(SPEC[provider]||[]).map(f=>(
            <Field key={f.id} {...f}
                   value={cState[f.id]||""}
                   onChange={v=>setCState(o=>({...o,[f.id]:v}))}/>
          ))}
          <label className="block mt-3 mb-1 text-green-200">
            If you use Teams/Personal, paste your <code>_account</code> cookie value here (optional):
          </label>
          <input
            className="w-full p-2 rounded bg-green-950 border border-green-700 text-green-100 text-xs"
            value={accountCookie}
            onChange={e=>setAccountCookie(e.target.value)}
            placeholder="Paste _account cookie value (optional)"
          />
          <button className="btn-primary mt-4"
                  disabled={!cookiesValid()}
                  onClick={()=>setStep(2)}>Continue</button>
        </div>
      )}

      {step===2&&(
        <div className="flex flex-col items-center p-6">
          <h3 className="text-green-300 mb-4">How many chats?</h3>
          {[10,15,30].map(n=>(
            <button key={n} className="btn-primary mb-2" onClick={()=>beginScrape(n)}>{n}</button>
          ))}
        </div>
      )}

      {step===3&&(
        <>
          <h3 className="text-green-300 mb-2">Scraping in progress...</h3>
          <div className="w-full h-4 bg-green-900 rounded mb-2">
            <div className="bg-green-400 h-4 rounded animate-pulse" style={{width:'100%'}}/>
          </div>
          <div className="text-green-400 text-center">Please wait while chats are being scraped...</div>
        </>
      )}

      {step===4&&(
        <>
          <h3 className="text-green-300 mb-2">Review {idx+1}/{chats.length}</h3>
          <input className="input-terminal w-full mb-2"
                 value={chat.title||""}
                 onChange={e=>update({title:e.target.value})}/>
          <select className="input-terminal w-full mb-2"
                  value={chat.model||""}
                  onChange={e=>update({model:e.target.value})}>
            {modelCodes.map(m=><option key={m.code} value={m.code}>
              {m.name} ({m.code})
            </option>)}
          </select>
          <select className="input-terminal w-full mb-2"
                  value={chat.primaryTopic||""}
                  onChange={e=>update({primaryTopic:e.target.value})}>
            <option value="">Primary topic *</option>
            {topics.map(t=><option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>)}
          </select>
          <select className="input-terminal w-full mb-2"
                  value={chat.secondaryTopic||""}
                  onChange={e=>update({secondaryTopic:e.target.value})}>
            <option value="">Secondary topic (optional)</option>
            {topics.map(t=><option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>)}
          </select>
          <textarea readOnly className="input-terminal w-full h-40 mb-2">{chat.content}</textarea>
          <button className="btn-primary mr-2" onClick={()=>save(true)}>Import</button>
          <button className="btn-secondary"      onClick={()=>save(false)}>Skip</button>
        </>
      )}
    </div>
  </div>
  );
}